/**
 * Forwards the GA `client_id` / `session_id` that gtag owns to the server as part of EPiServer Forms
 * submissions (read server-side by Ga4EventTracker). gtag keeps these identifiers correct across
 * cookie-format changes and returns them even when the GA cookies are HttpOnly or scoped to another domain.
 *
 * The values are sent as part of the user-initiated form submission (not stored client-side), so this works
 * regardless of cookie consent. The backend reads them from the request and falls back to cookie parsing /
 * a generated id when they are absent, so this is a progressive enhancement.
 *
 * Mechanism: when JavaScript is enabled EPiServer Forms submits over AJAX, building its own FormData from
 * only the fields it owns — the hidden ga_client_id/ga_session_id inputs are never serialised that way.
 * Just before the POST it raises a `formsStartSubmitting` jQuery event carrying that FormData by reference,
 * which is where we inject the identifiers. The hidden inputs remain as the non-JS (native POST) fallback.
 */

const CLIENT_ID_FIELD = "ga_client_id";
const SESSION_ID_FIELD = "ga_session_id";
const SCREEN_RESOLUTION_FIELD = "ga_screen_resolution";
const CONSENT_FIELD = "ga_consent";
const PAGE_TITLE_FIELD = "ga_page_title";
const PAGE_LOCATION_FIELD = "ga_page_location";
const PAGE_REFERRER_FIELD = "ga_page_referrer";

const FORMS_SELECTOR = ".EPiServerForms";
const SUBMIT_EVENT = "formsStartSubmitting";

const resolvedIdentifiers: Record<string, string> = {};

const captureIdentifier = (fieldName: string, value: string | undefined): void => {
  if (!value) {
    return;
  }

  resolvedIdentifiers[fieldName] = value;
};

// Forwards the current Cookiebot consent as a GA4 consent-state (`gcs`) string so the backend reports the
// real consent instead of a hardcoded value. `gcs` is a fixed two-flag format: `G1` + ad_storage +
// analytics_storage (1 = granted, 0 = denied), mapped from Cookiebot's marketing and statistics. The other
// categories (necessary, preferences) have no slot in `gcs` and are intentionally left out.
const captureConsent = (): void => {
  const consent = window.Cookiebot?.consent;
  if (!consent) {
    return;
  }

  const adStorage = consent.marketing ? "1" : "0";
  const analyticsStorage = consent.statistics ? "1" : "0";
  captureIdentifier(CONSENT_FIELD, `G1${adStorage}${analyticsStorage}`);
};

// Binds the GA identifiers into the FormData of the AJAX submit. The event is dispatched through Forms'
// own noConflict jQuery instance, so we must bind via epi.EPiServer.Forms.$ rather than the DOM/site jQuery.
// Returns false while that jQuery instance is not yet available so the caller can retry.
const bindSubmitHook = (): boolean => {
  const epiForms$ = window.epi?.EPiServer?.Forms?.$;
  if (!epiForms$) {
    return false;
  }

  document.querySelectorAll<HTMLElement>(FORMS_SELECTOR).forEach((form) => {
    epiForms$(form).on(SUBMIT_EVENT, ({ formData }) => {
      if (!formData) {
        return;
      }
      // Read the latest captured values at submit time; set (not append) so a re-submit can't duplicate them.
      Object.entries(resolvedIdentifiers).forEach(([fieldName, value]) => {
        formData.set(fieldName, value);
      });
    });
  });

  return true;
};

export const bootstrapGaFormIdentifiers = (): void => {
  const measurementId = window.ICE?.settings?.ga4MeasurementId;
  const forms = document.querySelectorAll<HTMLElement>(FORMS_SELECTOR);

  if (!measurementId || typeof gtag !== "function" || forms.length === 0) {
    return;
  }

  gtag("get", measurementId, "client_id", (value) => captureIdentifier(CLIENT_ID_FIELD, value));
  gtag("get", measurementId, "session_id", (value) => captureIdentifier(SESSION_ID_FIELD, value));

  // Screen resolution only exists client-side (window.screen); forward it so the backend can report a real
  // `sr` value instead of a hardcoded fallback.
  if (window.screen?.width && window.screen?.height) {
    captureIdentifier(SCREEN_RESOLUTION_FIELD, `${window.screen.width}x${window.screen.height}`);
  }

  // Page title only exists client-side (document.title); forward it so the backend can report a real `dt` value
  // per page instead of omitting it.
  captureIdentifier(PAGE_TITLE_FIELD, document.title);

  // The full page URL (`dl`) and the real referrer (`dr`) also only exist client-side. Forward them so the backend
  // reports the actual page instead of the bare host, and the real referrer instead of a fabricated self-referral.
  // captureIdentifier skips empty values, so a blank document.referrer (direct entry) is simply not sent.
  captureIdentifier(PAGE_LOCATION_FIELD, window.location.href);
  captureIdentifier(PAGE_REFERRER_FIELD, document.referrer);

  captureConsent();
  window.addEventListener("CookiebotOnConsentReady", captureConsent);

  // Forms' jQuery is set up by its prerequisite script, which may run after this. Retry briefly until ready.
  if (!bindSubmitHook()) {
    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (bindSubmitHook() || attempts >= 20) {
        window.clearInterval(timer);
      }
    }, 100);
  }
};
